import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, Tooltip, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import SiteWrapper from '../../SiteWrapper.react';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const GOOGLE_API_KEY = 'AIzaSyAPxZkM6L2aL0LEW_LyKG2EdMKW-e3-dU4';
const CORS_PROXY = 'https://cors-anywhere.herokuapp.com/';
const GOOGLE_DIRECTIONS_URL = 'https://maps.googleapis.com/maps/api/directions/json';
const NOMINATIM_URL = 'https://nominatim.openstreetmap.org/search';

// Función para decodificar polyline de Google
function decodePolyline(str) {
  let index = 0, lat = 0, lng = 0;
  const coordinates = [];
  
  while (index < str.length) {
    let b, shift = 0, result = 0;
    do {
      b = str.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    
    const deltaLat = ((result & 1) ? ~(result >> 1) : (result >> 1));
    lat += deltaLat;
    
    shift = 0;
    result = 0;
    do {
      b = str.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    
    const deltaLng = ((result & 1) ? ~(result >> 1) : (result >> 1));
    lng += deltaLng;
    
    coordinates.push([lat / 1e5, lng / 1e5]);
  }
  
  return coordinates;
}

let nextId = 1;

function makeIcon(color, text) {
  return L.divIcon({
    className: '',
    html: `<div style="
      background:${color};color:#fff;border-radius:50%;width:28px;height:28px;
      display:flex;align-items:center;justify-content:center;font-weight:bold;
      font-size:13px;border:2px solid #fff;box-shadow:0 2px 4px rgba(0,0,0,.4);
    ">${text}</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
}

function ClickHandler({ onAdd }) {
  useMapEvents({ click: (e) => onAdd({ id: nextId++, coords: [e.latlng.lat, e.latlng.lng], label: 'Punto en mapa' }) });
  return null;
}

function WaypointMarker({ wp, role, number, onDrag, onRemove }) {
  const icon = useMemo(() => {
    if (role === 'origen') return makeIcon('#16a34a', 'O');
    if (role === 'destino') return makeIcon('#dc2626', 'D');
    return makeIcon('#2563eb', number);
  }, [role, number]);

  const roleLabel = role === 'origen' ? '🟢 Origen' : role === 'destino' ? '🔴 Destino' : `${number}.`;

  return (
    <Marker
      position={wp.coords}
      icon={icon}
      draggable
      eventHandlers={{ dragend: (e) => { const { lat, lng } = e.target.getLatLng(); onDrag(wp.id, [lat, lng]); } }}
    >
      <Tooltip direction="top" offset={[0, -14]} permanent>
        <span style={{ fontSize: 11 }}><b>{roleLabel}</b> {wp.label}</span>
      </Tooltip>
      <Popup>
        <b>{roleLabel} {wp.label}</b><br />
        {wp.coords[0].toFixed(5)}, {wp.coords[1].toFixed(5)}<br />
        <button onClick={() => onRemove(wp.id)} style={{ marginTop: 4, cursor: 'pointer', color: 'red', border: 'none', background: 'none', padding: 0, textDecoration: 'underline' }}>
          Eliminar
        </button>
      </Popup>
    </Marker>
  );
}

const RuteadorPage = () => {
  const [waypoints, setWaypoints] = useState([]);
  const [originId, setOriginId] = useState(null);
  const [destinationId, setDestinationId] = useState(null);
  const [route, setRoute] = useState(null);
  const [info, setInfo] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [coordText, setCoordText] = useState('');
  const [dragIdx, setDragIdx] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [showTraffic, setShowTraffic] = useState(false);

  // Construir lista ordenada: origen primero, destino último, intermedios en medio
  const orderedPoints = useMemo(() => {
    const origin = waypoints.find((w) => w.id === originId);
    const dest = waypoints.find((w) => w.id === destinationId);
    const mid = waypoints.filter((w) => w.id !== originId && w.id !== destinationId);
    const result = [];
    if (origin) result.push(origin);
    result.push(...mid);
    if (dest) result.push(dest);
    return result;
  }, [waypoints, originId, destinationId]);

  const fetchRoute = useCallback(async (pts, hasOrigin, hasDest) => {
    if (pts.length < 2) { setRoute(null); setInfo(null); return; }
    
    const origin = `${pts[0].coords[0]},${pts[0].coords[1]}`;
    const destination = `${pts[pts.length-1].coords[0]},${pts[pts.length-1].coords[1]}`;
    const waypoints = pts.slice(1, -1).map(wp => `${wp.coords[0]},${wp.coords[1]}`).join('|');
    
    // Usar el backend local en lugar de llamada directa
    const requestBody = {
      origin,
      destination,
      waypoints: waypoints || null,
      includeTraffic: true
    };

    try {
      const response = await fetch('/api/directions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });
      
      const data = await response.json();
      
      if (data.routes?.[0]) {
        const route = data.routes[0];
        const decodedPath = decodePolyline(route.overview_polyline.points);
        
        setRoute(decodedPath);
        setInfo({ 
          distance: (route.legs.reduce((sum, leg) => sum + leg.distance.value, 0) / 1000).toFixed(1),
          duration: Math.round(route.legs.reduce((sum, leg) => sum + (leg.duration_in_traffic?.value || leg.duration.value), 0) / 60)
        });
      }
    } catch (error) {
      console.error('Error fetching route:', error);
      // Fallback a OSRM si falla el backend
      const coords = pts.map((wp) => `${wp.coords[1]},${wp.coords[0]}`).join(';');
      const osrmUrl = `https://router.project-osrm.org/route/v1/driving/${coords}?overview=full&geometries=geojson`;
      
      try {
        const osrmResponse = await fetch(osrmUrl);
        const osrmData = await osrmResponse.json();
        if (osrmData.routes?.[0]) {
          setRoute(osrmData.routes[0].geometry.coordinates.map(([lng, lat]) => [lat, lng]));
          setInfo({ 
            distance: (osrmData.routes[0].distance / 1000).toFixed(1), 
            duration: Math.round(osrmData.routes[0].duration / 60) 
          });
        }
      } catch (fallbackError) {
        setRoute(null); 
        setInfo(null);
      }
    }
  }, []);

  useEffect(() => {
    fetchRoute(orderedPoints, originId !== null, destinationId !== null);
  }, [orderedPoints, originId, destinationId, fetchRoute]);

  const addPoint = useCallback((wp) => setWaypoints((prev) => [...prev, wp]), []);

  const updatePoint = useCallback((id, coords) => {
    setWaypoints((prev) => prev.map((w) => w.id === id ? { ...w, coords } : w));
  }, []);

  const removePoint = useCallback((id) => {
    setWaypoints((prev) => prev.filter((w) => w.id !== id));
    setOriginId((prev) => prev === id ? null : prev);
    setDestinationId((prev) => prev === id ? null : prev);
  }, []);

  const toggleOrigin = useCallback((id) => {
    setOriginId((prev) => prev === id ? null : id);
    setDestinationId((prev) => prev === id ? null : prev);
  }, []);

  const toggleDestination = useCallback((id) => {
    setDestinationId((prev) => prev === id ? null : id);
    setOriginId((prev) => prev === id ? null : prev);
  }, []);

  const handleGeocode = (e) => {
    e.preventDefault();
    if (!searchText.trim()) return;
    fetch(`${NOMINATIM_URL}?q=${encodeURIComponent(searchText)}&format=json&limit=5&addressdetails=1`)
      .then((r) => r.json())
      .then((data) => {
        if (data.length > 0) setSearchResults(data);
        else { alert('No se encontraron resultados'); setSearchResults([]); }
      })
      .catch(() => alert('Error en búsqueda'));
  };

  const selectResult = (item) => {
    addPoint({ id: nextId++, coords: [parseFloat(item.lat), parseFloat(item.lon)], label: item.display_name.split(',').slice(0, 3).join(',') });
    setSearchResults([]);
    setSearchText('');
  };

  const handleCoords = (e) => {
    e.preventDefault();
    const parts = coordText.split(',').map((s) => parseFloat(s.trim()));
    if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
      addPoint({ id: nextId++, coords: parts, label: coordText.trim() });
      setCoordText('');
    } else {
      alert('Formato: latitud, longitud');
    }
  };

  const clearAll = () => { setWaypoints([]); setOriginId(null); setDestinationId(null); setRoute(null); setInfo(null); };

  const drawRoute = () => {
    if (waypoints.length >= 2) {
      fetchRoute(orderedPoints, originId !== null, destinationId !== null);
    }
  };

  const reorder = (from, to) => {
    setWaypoints((prev) => {
      const n = [...prev];
      const [item] = n.splice(from, 1);
      n.splice(to, 0, item);
      return n;
    });
  };

  const panelStyle = {
    position: 'absolute', top: 80, left: 10, zIndex: 1000,
    background: 'white', padding: 12, borderRadius: 6,
    boxShadow: '0 2px 8px rgba(0,0,0,0.3)', width: 320, maxHeight: '85%', overflowY: 'auto',
  };
  const inputStyle = { width: '100%', padding: '4px 6px', boxSizing: 'border-box' };
  const btnStyle = { padding: '4px 10px', cursor: 'pointer', marginTop: 4 };
  const roleBtnBase = { cursor: 'pointer', border: '1px solid', borderRadius: 3, padding: '1px 5px', fontSize: 10, marginLeft: 3 };

  return (
    <SiteWrapper>
      <div style={{ width: '100%', height: 'calc(100vh - 60px)', position: 'relative' }}>
        <div style={panelStyle}>
          <b>Ruteador</b>
          <form onSubmit={handleGeocode} style={{ marginTop: 8 }}>
            <input style={inputStyle} placeholder="Buscar dirección..." value={searchText} onChange={(e) => { setSearchText(e.target.value); setSearchResults([]); }} />
            <button type="submit" style={btnStyle}>Buscar</button>
          </form>
          {searchResults.length > 0 && (
            <ul style={{ listStyle: 'none', padding: 0, margin: '4px 0', fontSize: 12, maxHeight: 150, overflowY: 'auto', border: '1px solid #ddd', borderRadius: 4 }}>
              {searchResults.map((item, i) => (
                <li key={i} onClick={() => selectResult(item)}
                  style={{ padding: '5px 8px', cursor: 'pointer', borderBottom: '1px solid #eee', background: i % 2 ? '#f9f9f9' : '#fff' }}
                  onMouseEnter={(e) => e.target.style.background = '#e0edff'}
                  onMouseLeave={(e) => e.target.style.background = i % 2 ? '#f9f9f9' : '#fff'}
                >📍 {item.display_name}</li>
              ))}
            </ul>
          )}
          <form onSubmit={handleCoords} style={{ marginTop: 8 }}>
            <input style={inputStyle} placeholder="lat, lng (ej: 19.4326, -99.1332)" value={coordText} onChange={(e) => setCoordText(e.target.value)} />
            <button type="submit" style={btnStyle}>Agregar</button>
          </form>
          <div style={{ marginTop: 6, fontSize: 12, color: '#666' }}>
            También puedes hacer clic en el mapa para agregar puntos
          </div>
          <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', fontSize: 12 }}>
            <input 
              type="checkbox" 
              id="traffic-toggle" 
              checked={showTraffic} 
              onChange={(e) => setShowTraffic(e.target.checked)}
              style={{ marginRight: 6 }}
            />
            <label htmlFor="traffic-toggle" style={{ cursor: 'pointer' }}>
              🚦 Mostrar capa de tráfico
            </label>
          </div>
          {waypoints.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <b>Puntos ({waypoints.length})</b>
                <div>
                  <button onClick={drawRoute} disabled={waypoints.length < 2} style={{ ...btnStyle, color: waypoints.length >= 2 ? 'blue' : '#ccc', fontSize: 12, marginRight: 4 }}>
                    Dibujar ruta
                  </button>
                  <button onClick={clearAll} style={{ ...btnStyle, color: 'red', fontSize: 12 }}>Limpiar todo</button>
                </div>
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: '4px 0', fontSize: 12 }}>
                {waypoints.map((wp, i) => {
                  const isOrigin = wp.id === originId;
                  const isDest = wp.id === destinationId;
                  return (
                    <li key={wp.id} draggable
                      onDragStart={() => setDragIdx(i)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => { if (dragIdx !== null && dragIdx !== i) reorder(dragIdx, i); setDragIdx(null); }}
                      style={{
                        marginBottom: 3, padding: '4px 6px', cursor: 'grab', borderRadius: 4,
                        background: dragIdx === i ? '#e0edff' : isOrigin ? '#e6f9e6' : isDest ? '#fde8e8' : '#f8f8f8',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      }}
                    >
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        ☰ {isOrigin && '🟢 '}{isDest && '🔴 '}{wp.label}
                      </span>
                      <span style={{ whiteSpace: 'nowrap', display: 'flex', alignItems: 'center' }}>
                        <button onClick={() => toggleOrigin(wp.id)}
                          style={{ ...roleBtnBase, background: isOrigin ? '#16a34a' : '#fff', color: isOrigin ? '#fff' : '#16a34a', borderColor: '#16a34a' }}
                          title={isOrigin ? 'Quitar origen' : 'Marcar como origen'}>O</button>
                        <button onClick={() => toggleDestination(wp.id)}
                          style={{ ...roleBtnBase, background: isDest ? '#dc2626' : '#fff', color: isDest ? '#fff' : '#dc2626', borderColor: '#dc2626' }}
                          title={isDest ? 'Quitar destino' : 'Marcar como destino'}>D</button>
                        <span onClick={() => removePoint(wp.id)} style={{ color: 'red', cursor: 'pointer', marginLeft: 5 }}>✕</span>
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          {info && (
            <div style={{ marginTop: 8, padding: '6px 8px', background: '#f0f7ff', borderRadius: 4, fontSize: 13 }}>
              <b>{info.distance} km</b> — {info.duration} min
            </div>
          )}
        </div>

        <MapContainer center={[19.386, -99.101]} zoom={12} style={{ width: '100%', height: '100%' }}>
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' />
          {showTraffic && (
            <TileLayer
              url="https://mt1.google.com/vt/lyrs=h@221097413,traffic&x={x}&y={y}&z={z}"
              attribution='&copy; Google'
              opacity={0.7}
            />
          )}
          <ClickHandler onAdd={addPoint} />
          {orderedPoints.map((wp, i) => {
            const role = wp.id === originId ? 'origen' : wp.id === destinationId ? 'destino' : null;
            const midIdx = orderedPoints.filter((w, j) => j < i && w.id !== originId && w.id !== destinationId).length + 1;
            return (
              <WaypointMarker key={wp.id} wp={wp} role={role} number={midIdx} onDrag={updatePoint} onRemove={removePoint} />
            );
          })}
          {route && <Polyline positions={route} color="blue" weight={5} opacity={0.7} />}
        </MapContainer>
      </div>
    </SiteWrapper>
  );
};

export default RuteadorPage;
